import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  HttpOptions,
  InMemoryCache,
  NormalizedCacheObject,
  split
} from '@apollo/client'
import { getOperationName } from '@apollo/client/utilities'
import config from 'config'
import { IncomingHttpHeaders, IncomingMessage } from 'http'
import { useMemo } from 'react'
import PossibleTypesResultData from '~gql-types/graphql'

import { getNetworkSubdomain } from 'src/lib/middleware'
import { NetworkPageContext } from 'src/types/next'
import { getCsrf } from 'util/session'

const internalGraphqlUrl = `${config.GALACTUS_URL}/graphql`
const publicGraphqlUrl = `${config.NEXT_PUBLIC_GALACTUS_URL}/graphql`

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null

const isServer = typeof window === 'undefined'

interface ApolloClientContext {
  req?: IncomingMessage
  res?: $TSFixMe
  asPath?: $TSFixMe
  pathname?: $TSFixMe
}

interface InitApollo {
  ctx?: ApolloClientContext
  flags?: FlagsMap
}

type FlagValue = {
  description: string
  value: boolean
}

type FlagsMap = Record<string, FlagValue>

const getHeaders = (
  req: IncomingMessage | undefined,
  ctx: ApolloClientContext
) => {
  const headers: Record<string, $TSFixMe> = {}

  if (config.NEXT_PUBLIC_DD_VERSION) {
    headers['x-yellowdig-version'] = config.NEXT_PUBLIC_DD_VERSION
  }

  if (ctx.asPath) {
    headers['x-yellowdig-url'] = ctx.asPath
  }

  if (ctx.pathname) {
    headers['x-yellowdig-path'] = ctx.pathname
  }

  if (ctx.req && ctx.req.headers['user-agent']) {
    headers['x-yellowdig-user-agent'] = ctx.req.headers['user-agent']
  }

  if (ctx.req && ctx.res) {
    const subdomain = getNetworkSubdomain(ctx as NetworkPageContext)
    headers['x-yellowdig-network-name'] = subdomain
  }

  headers.cookie = req!.headers.cookie

  return headers
}

const createCsrfLink = (ctx?: ApolloClientContext) => {
  if (ctx && ctx.req) {
    const { req } = ctx
    return new ApolloLink((operation, forward) => {
      operation.setContext(({ headers }: { headers: IncomingHttpHeaders }) => ({
        headers: {
          'x-yellowdig-csrf-token': getCsrf(req),
          ...headers
        }
      }))
      return forward(operation)
    })
  }
  return new ApolloLink((operation, forward) => {
    operation.setContext(({ headers }: { headers: IncomingHttpHeaders }) => ({
      headers: {
        'x-yellowdig-csrf-token': getCsrf(),
        ...headers
      }
    }))
    return forward(operation)
  })
}

const createDebugHeadersLink = (ctx?: ApolloClientContext) => {
  if (ctx && ctx.req) {
    const { req } = ctx
    return new ApolloLink((operation, forward) => {
      operation.setContext(({ headers }: { headers: IncomingHttpHeaders }) => {
        const generatedHeaders = getHeaders(req, ctx)
        return {
          headers: {
            ...generatedHeaders,
            ...headers
          }
        }
      })
      return forward(operation)
    })
  }
  return new ApolloLink((operation, forward) => {
    operation.setContext(({ headers }: { headers: IncomingHttpHeaders }) => {
      const clientHeaders = isServer
        ? {}
        : {
            'x-yellowdig-user-agent':
              window.navigator && window.navigator.userAgent,
            'x-yellowdig-url': window.location.pathname
          }
      const subdomain = getNetworkSubdomain({})

      return {
        headers: {
          'x-yellowdig-network-name': subdomain,
          ...clientHeaders,
          ...headers
        }
      }
    })
    return forward(operation)
  })
}

/* eslint-disable @typescript-eslint/no-require-imports */
function createIsomorphicLink(
  ctx: ApolloClientContext | undefined,
  flags: FlagsMap | undefined
) {
  const stellateUri = config.NEXT_PUBLIC_STELLATE_CDN_ENDPOINT
  const originUri = isServer ? internalGraphqlUrl : publicGraphqlUrl
  const stellateEnabled = flags && Boolean(flags?.gqlEdgeCache?.value)

  const STELLATE_OPERATION_NAMES = [
    'FetchCommentData',
    'CreateComment',
    'DeleteComment',
    'EditComment'
  ]

  const httpLinkOptions = (uri?: string) => {
    if (ctx && ctx.req) {
      const { req } = ctx

      const httpLinkParam: HttpOptions = {
        uri,
        credentials: 'same-origin',
        headers: getHeaders(req, ctx)
      }

      if (uri && uri.startsWith('https')) {
        const https = require('https')
        httpLinkParam.fetchOptions = {
          agent: new https.Agent({ rejectUnauthorized: false }),
          cache: 'no-store'
        }
      }

      return httpLinkParam
    }
    return {
      uri,
      credentials: 'include'
    }
  }

  const stellateLink = new HttpLink(httpLinkOptions(stellateUri))
  const originLink = new HttpLink(httpLinkOptions(originUri))

  return split(
    operation => {
      const opName = getOperationName(operation.query)
      return (
        stellateUri &&
        stellateEnabled &&
        opName &&
        STELLATE_OPERATION_NAMES.includes(opName)
      )
    },
    stellateLink,
    originLink
  )
}

/* eslint-enable @typescript-eslint/no-require-imports */
const cacheOptions = {
  possibleTypes: PossibleTypesResultData.possibleTypes,
  typePolicies: {
    NetworkMember: {
      keyFields: ['networkId', 'userId']
    },
    BoardMember: {
      keyFields: ['boardId', 'userId']
    }
  }
}

function createApolloClient(
  ctx: ApolloClientContext | undefined,
  flags?: FlagsMap
) {
  return new ApolloClient({
    ssrMode: isServer,
    cache: new InMemoryCache(cacheOptions),
    connectToDevTools: true,
    link: from([
      createCsrfLink(ctx),
      createDebugHeadersLink(ctx),
      createIsomorphicLink(ctx, flags)
    ])
  })
}

export function initializeApollo({ ctx, flags }: InitApollo) {
  const _apolloClient =
    apolloClient ?? createApolloClient(ctx || undefined, flags)
  // For SSG and SSR always create a new Apollo Client
  if (isServer) return _apolloClient
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient

  return _apolloClient
}

export function useApollo(pageProps: $TSFixMe) {
  const store = useMemo(
    () =>
      initializeApollo({
        flags: pageProps.flags
      }),
    [pageProps.flags]
  )
  return store
}
